import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { PasswordModule } from 'primeng/password';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Toast } from '@/shared/components/toast/toast';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardContainer } from 'src/app/shared/components/card-container/card-container';
import { ConfigEmailService } from '@/core/api/sicom-api/sistema/config-email/config-email.service';
import { EmailService } from '@/core/api/sicom-api/sistema/email/email.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-email',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, ButtonModule, DialogModule, InputTextModule, InputNumberModule, PasswordModule, ToggleSwitchModule, Toast, SelectModule, CardContent, CardContainer, CanDirective],
    providers: [MessageService],
    templateUrl: './email.html',
    styleUrl: './email.scss'
})
/** Configuração de e-mail do sistema (servidor SMTP, credenciais e remetente). */
export class Email implements OnInit {
    protected readonly roleEmailEditar = PERMISSOES.configuracoes.email.editar;

    private readonly messageService = inject(MessageService);
    private readonly configEmailService = inject(ConfigEmailService);
    private readonly emailService = inject(EmailService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly permissionService = inject(PermissionService);

    host = signal('');
    porta = signal<number>(587);
    username = signal('');
    password = signal('');
    protocol = signal('smtp');
    protocolOptions = [
        { label: 'SMTP', value: 'smtp' },
        { label: 'SMTPS', value: 'smtps' }
    ];
    auth = signal(true);
    starttls = signal(true);
    senderAddress = signal('');

    configExiste = signal(false);
    carregando = signal(false);
    salvando = signal(false);
    enviandoTeste = signal(false);
    dialogTeste = signal(false);
    emailDestino = signal('');

    senderAddressInvalido = signal(false);
    hostInvalido = signal(false);

    ngOnInit(): void {
        this.carregarConfiguracao();
    }

    private carregarConfiguracao(): void {
        // Sem leitura de e-mail: não busca (evita 403 → toast); mantém o formulário vazio.
        if (!this.permissionService.can(PERMISSOES.configuracoes.email.visualizar)) {
            return;
        }

        this.carregando.set(true);
        this.configEmailService
            .buscar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (config) => {
                    if (config) {
                        this.host.set(config.host ?? '');
                        this.porta.set(config.port ?? 587);
                        this.username.set(config.username ?? '');
                        this.protocol.set(config.protocol ?? 'smtp');
                        this.auth.set(config.auth ?? true);
                        this.starttls.set(config.starttls ?? true);
                        this.senderAddress.set(config.senderAddress ?? '');
                        this.configExiste.set(true);
                    }
                    this.carregando.set(false);
                },
                error: () => {
                    this.carregando.set(false);
                }
            });
    }

    abrirDialogTeste(): void {
        this.emailDestino.set('');
        this.dialogTeste.set(true);
    }

    enviarEmailTeste(): void {
        const dest = this.emailDestino().trim();
        if (!dest) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe um e-mail de destino.' });
            return;
        }
        this.enviandoTeste.set(true);
        this.emailService
            .enviarTeste([dest])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.enviandoTeste.set(false);
                    this.dialogTeste.set(false);
                    this.messageService.add({ severity: 'success', summary: 'Enviado', detail: `E-mail de teste enviado para ${dest}.` });
                },
                error: () => {
                    this.enviandoTeste.set(false);
                }
            });
    }

    salvar(): void {
        const hostValido = !!this.host().trim();
        const senderValido = !!this.senderAddress().trim();

        this.hostInvalido.set(!hostValido);
        this.senderAddressInvalido.set(!senderValido);

        if (!hostValido || !senderValido || !this.porta()) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Host, porta e e-mail remetente são obrigatórios.' });
            return;
        }
        this.salvando.set(true);
        this.configEmailService
            .salvar({
                host: this.host().trim(),
                port: this.porta(),
                username: this.username().trim() || null,
                password: this.password() || null,
                protocol: this.protocol().trim() || 'smtp',
                auth: this.auth(),
                starttls: this.starttls(),
                senderAddress: this.senderAddress().trim()
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvando.set(false);
                    this.configExiste.set(true);
                    this.password.set('');
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Configurações salvas com sucesso!' });
                },
                error: () => {
                    this.salvando.set(false);
                }
            });
    }
}
